/* eslint-disable @typescript-eslint/no-dynamic-delete */
// @ts-strict-ignore
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { PromiseQueue } from 'rainbow-web-sdk/utils/promiseQueue';
import { CallDirection, CallRB, CallStatus, CallType } from 'rainbow-web-sdk/models/call.model';
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { BubbleServiceRB } from '../bubbles/bubble.service';
import { MPCallState, WEBRTC_GATEWAY_SVC } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';
import { TelephonyService } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Office365AdService } from 'rainbow-web-sdk/services/directory/office365ad.service';
import { CompanyDirectoryService } from 'rainbow-web-sdk/services/company/company-directory.service';

export interface ITelephonyEventHandler {
    attachHandlers(): void;
    detachHandlers(): void;
}

/**
 * @class
 * @public
 * @name TelephonyHybridEventHandler
 * @description
 * This class is used to represent the handler of hybrid telephony (oxe/oxo/third party) events
 */
export class TelephonyHybridEventHandler implements ITelephonyEventHandler {

    // Service dependencies
    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private roomService: BubbleServiceRB = BubbleServiceRB.getInstance();
    private office365AdService: Office365AdService = Office365AdService.getInstance();
    private companyDirectoryService: CompanyDirectoryService = CompanyDirectoryService.getInstance();

    // Private members
    private messageHandlerRef: any = null;
    private promiseQueue: PromiseQueue;
    private reservationCalls: any = {};

    // Static members
    private static CALLSERVICE_NS: string = "urn:xmpp:pbxagent:callservice:1";
    private static CallFailureLabels = {
        DESTNOTOBTAINABLE: "outOfService",
        DONOTDISTURB: "dnd",
        TRUNKSBUSY: "trunksbusy",
        BUSY: "busy"
    };

    /**
     * Constructor of TelephonyHybridEventHandler instance
     * @public
     * @param {any} telephonyService reference to the telephony service
     * @memberof TelephonyHybridEventHandler
     */
    constructor(
        private telephonyService: TelephonyService) {
        this.promiseQueue = PromiseQueue.create();
    }

    /**
     * Create the handler of telephony events for hybrid telephony (OXE / OXO / third party systems)
     * @public
     * @static
     * @param {any} telephonyService reference to the telephony service
     * @returns {TelephonyHybridEventHandler} Instance of newly created TelephonyHybridEventHandler
     * @memberof TelephonyHybridEventHandler
     */
    public static create(telephonyService: any) {
        return new TelephonyHybridEventHandler(telephonyService);
    }

    /*************************************************************/
    /*          XMPP HANDLER ATTACH / DETACH                     */
    /*************************************************************/

    /**
     * Attach the xmpp handlers to callbacks
     * @public
     * @memberof TelephonyHybridEventHandler
     */
    public attachHandlers(): void {
        this.detachHandlers();
        this.messageHandlerRef = this.xmppService.addHandler((stanza) => { this.onCallserviceMessageReceived(stanza); return true; }, TelephonyHybridEventHandler.CALLSERVICE_NS, "message", null);
    }

    /**
     * Detach the xmpp handlers
     * @public
     * @memberof TelephonyHybridEventHandler
     */
    public detachHandlers(): void {
        if (this.messageHandlerRef) {
            this.xmppService.deleteHandler(this.messageHandlerRef);
            this.messageHandlerRef = null;
        }
    }

    /*************************************************************/
    /* CALLSERVICE EVENT HANDLERS                                */
    /*************************************************************/

    /**
     * Main handling callback method for received telephony events
     * @public
     * @param {any} stanza Received telephony event
     * @returns {boolean} true indicates successful handling of event
     * @memberof TelephonyHybridEventHandler
     */
    public onCallserviceMessageReceived(stanza: any): boolean {
        try {
            const stanzaElem = $(stanza);

            // Ignore "Offline" message
            const delay = stanzaElem.find("delay");
            if (delay.text() === "Offline Storage") { return true; }

            const supervisionMessage = stanzaElem.find("forwarded[xmlns='urn:xmpp:supervision:0']");
            if (supervisionMessage?.length) {
                // this is a supervision message broadcasted to supervisors: it will be handled in supervision service and must be ignored here
                // this.logger.debug("[TelephonyHybridEventHandler] onCallserviceMessageReceived -- supervision Message => IGNORED");
                return true;
            }

            // Extract payload element
            const actionElem = stanzaElem.find("callservice").children();
            const tagNames = actionElem.prop("tagName").split(":");
            const actionElemName = tagNames[tagNames.length - 1].toLowerCase();
            const callId = actionElem.attr("callId");
            const activeCallId = actionElem.attr("activeCallId");
            const callIdForLogs = (callId || activeCallId) ? (" -- " + (callId || activeCallId)) : "";
            let evtDeviceType = actionElem.attr("deviceType");
            const displayDeviceType = evtDeviceType ? " -- " + evtDeviceType : "";

            const oldCallId = actionElem.attr("oldCallId");
            const oldCallIdForLogs = oldCallId ? " -- old " + oldCallId : "";

            // newCallId is usually an attribute; but in conference event, it is a sub element
            const newCallId = actionElem.attr("newCallId") || actionElem.find("newCallId")?.text();
            const newCallIdForLogs = newCallId ? " -- new " + newCallId : "";

            const heldCallId = actionElem.attr("heldCallId");
            const heldCallIdForLogs = heldCallId ? " -- and " + heldCallId : "";

            const primaryOldCallId = actionElem.find("primaryOldCallId")?.text();
            const secondaryOldCallId = actionElem.find("secondaryOldCallId")?.text();

            const direction: string = actionElem.attr("type");

            if (!evtDeviceType) { evtDeviceType = "MAIN"; }
            const autoOffHookresource: string = actionElem.attr("resource");

            const cfgNomadic = this.telephonyService.getNomadicObject();
            const cfgOfficePhone = cfgNomadic ? (cfgNomadic.makeCallInitiatorIsMain || !cfgNomadic.modeActivated) : true;
            //CRRAINB-13957++
            let mediaPillarEngaged = false;
            let mediaPillarSelected = false;
            if (!cfgOfficePhone) {
                mediaPillarSelected = this.telephonyService.isMediaPillarUserSelected();
                mediaPillarEngaged = this.telephonyService.isMediaPillarAvailable() && mediaPillarSelected;
            }//CRRAINB-13957--

            const callStateEvent = !(["messaging", "voicemessages", "forwarded", "nomadicstatus", "opendesktop", "dynamicstate"].includes(actionElemName));

            if (callStateEvent
                && (!this.telephonyService.started
                    || ((((evtDeviceType === "MAIN") && !cfgOfficePhone && !autoOffHookresource) || ((evtDeviceType === "SECONDARY") && (cfgOfficePhone || (!mediaPillarEngaged && mediaPillarSelected)))) && !this.existingCall(callId)))
            ) {
                this.logger.info("[TelephonyHybridEventHandler] onCallserviceMessageReceived -- " + actionElemName.toUpperCase() + callIdForLogs + oldCallIdForLogs + displayDeviceType + " => IGNORED");
                return true;
            }
            this.logger.info("[TelephonyHybridEventHandler] [TEL][EVENT] onCallserviceMessageReceived -- " + actionElemName.toUpperCase()
                + (actionElemName.toUpperCase() === "DELIVERED" ? (" " + direction) : "") // delivered direction
                + ((actionElemName.toUpperCase() === "TRANSFERCALL" && heldCallId) ? " (initiator)" : "") // transfer initiator
                + ((actionElemName.toUpperCase() === "CONFERENCED" && secondaryOldCallId) ? " (initiator)" : "") // conference initiator
                + ((actionElemName.toUpperCase() === "CONFERENCED") ? (" " + primaryOldCallId + ((secondaryOldCallId ? (" + " + secondaryOldCallId) : ""))) : "") // conference old calls' ids
                + callIdForLogs + oldCallIdForLogs + newCallIdForLogs + heldCallIdForLogs + displayDeviceType);

            // Handle the event
            switch (actionElemName) {
                case "initiated": this.promiseQueue.add(() => { return this.onInitiatedEvent(actionElem); }); break;
                case "originated": this.promiseQueue.add(() => { return this.onOriginatedEvent(actionElem); }); break;
                case "delivered": this.promiseQueue.add(() => { return this.onDeliveredEvent(actionElem); }); break;
                case "queued": this.promiseQueue.add(() => { return this.onQueuedEvent(actionElem); }); break;
                case "failed": this.promiseQueue.add(() => { return this.onFailCallEvent(actionElem); }); break;
                case "updatecall": this.promiseQueue.add(() => { return this.onUpdateCallEvent(actionElem); }); break;
                case "updateparty": this.promiseQueue.add(() => { return this.onUpdatePartyEvent(actionElem); }); break;
                case "established": this.promiseQueue.add(() => { return this.onEstablishedEvent(actionElem); }); break;
                case "holdcall": case "held": this.promiseQueue.add(() => { return this.onHeldEvent(actionElem); }); break; // OLD SYNTAX TO BE REMOVED
                case "retrievecall": this.promiseQueue.add(() => { return this.onRetrieveCallEvent(actionElem); }); break;
                case "transfercall": case "transferred": this.promiseQueue.add(() => { return this.onTransferEvent(actionElem); }); break; // OLD SYNTAX TO BE REMOVED
                case "conferenced": this.promiseQueue.add(() => { return this.onConferenceEvent(actionElem); }); break;
                case "diverted": this.promiseQueue.add(() => { return this.onDivertedEvent(actionElem); }); break;
                case "callsubject": this.promiseQueue.add(() => { return this.onCallSubjectEvent(actionElem); }); break;
                case "connectioncleared": this.promiseQueue.add(() => { return this.onClearCallEvent(actionElem); }); break;
                case "forwarded": this.promiseQueue.add(() => { return this.onCallForwardedEvent(actionElem); }); break;
                case "nomadicstatus": this.promiseQueue.add(() => { return this.onCallNomadicEvent(actionElem); }); break;
                case "opendesktop": this.logger.info("[TelephonyHybridEventHandler] openDesktop detected "); break;
                case "dynamicstate": this.promiseQueue.add(() => { return this.onOxeDynamicStateEvent(actionElem); }); break;
                default: break;
            }
            return true;
        }
        catch (error) {
            this.logger.error("[TelephonyHybridEventHandler] onCallserviceMessageReceived -- failure -- " + error.message);
            return true;
        }
    }

    /**
     * Indicates whether a call exists with the given call identifier
     * @private
     * @param {string} callId call identifier
     * @returns {boolean} true if a call exists
     * @memberof TelephonyHybridEventHandler
     */
    private existingCall(callId: string): boolean {
        const exist = Boolean(this.telephonyService.calls[callId]);
        this.logger.debug("[TelephonyHybridEventHandler] existingCall -- " + callId + " => " + exist);
        return exist;
    }

    /**
     * Handler of INITIATED event
     * @private
     * @param {any} initiatedElem Initiated sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onInitiatedEvent(initiatedElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onInitiatedEvent");

        try {
            const call = await this.getCall(initiatedElem, false, true); //true in order to search in pending makecalls

            const deviceState = initiatedElem.attr("deviceState");
            const newConnectionId = initiatedElem.attr("newCallId");

            if (newConnectionId !== undefined) {
                // OXO in computer mode usecase
                const newCall = await this.getOrCreateCall(newConnectionId);

                this.logger.info("[TelephonyHybridEventHandler] onInitiatedEvent : changed call id from " + call.id + " to " + newConnectionId);

                // compute relevantEqtId and isSecondary properties of new call
                this.computeCallRelevancy(initiatedElem, newCall);

                // copy status from old call to new call
                if (deviceState === "LCI_CONNECTED") {
                    newCall.setStatus(CallStatus.ACTIVE);
                } else if (call.makecallCtiExtern) {
                    newCall.setStatus(CallStatus.DIALING);
                } else {
                    newCall.setStatus(call.callStatus);
                }
                // copy contact from old call to new call
                newCall.setContact(call.contact);
                // copy isOwner from old call to new call
                newCall.isOwner = call.isOwner;
                newCall.isMPAudioOwner = call.isMPAudioOwner;
                newCall.setDirection(CallDirection.OUTGOING);

                if (call.isInExternalWindow) {
                    this.eventService.publish("ON_REPLACE_CALLS_IN_EXTERNAL_POPOUT", newCall, call);
                }

                // Media Pillar ++: swap callref in media pillar context
                if (call.isMediaPillarCall()) {
                    this.eventService.publish("ON_REPLACE_CALLREFS_REQUEST", newCall, call);
                } // Media Pillar --

                // set old call status to ENDED
                this.telephonyService.clearCall(call);
                this.sendCallUpdateEvent(call);

                // notify new call
                this.sendCallUpdateEvent(newCall);

                // remove old call
                delete this.telephonyService.calls[call.id];
            } else {
                // Standard usecase
                this.computeCallRelevancy(initiatedElem, call);
                call.setDirection(CallDirection.OUTGOING);
                await this.checkMakecallCtiAndSendEvent(call, initiatedElem);
            }
        }
        catch (error) {
            const errorMessage = "onInitiatedEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    private async checkMakecallCtiAndSendEvent(call: CallRB, initiatedElem: any): Promise<void> {
        const cause: string = initiatedElem.attr("cause");
        const resource: string = initiatedElem.attr("resource");

        const isAutoAnswerMakecallActivated = (this.settingsService.getSetting("autoAnswer") === "true") && (this.settingsService.getSetting("autoAnswerByDeviceType") === "desktop");
        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_MAKE_CALL_CTI_APP) && call.isSecondary && !resource && !call.isOwner && cause === "MAKECALL" && isAutoAnswerMakecallActivated && this.contactService.isSingleComputerClient()) {
            call.autoAnswer = true;
        }
        if ((this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_MAKE_CALL_CTI_APP) && call.isSecondary && !resource && !call.isOwner && cause === "MAKECALL") || cause === "CALLBACK") {
            this.logger.info("[TelephonyHybridEventHandler] TELEPHONY_MAKE_CALL_CTI_APP - 3pcc activated");
            const contact = await this.contactService.getOrCreateContact("", "000");

            call.setContact(contact);
            call.setStatus(CallStatus.RINGING_INCOMMING);
            call.lastCause = cause ? cause : "";
            call.makecallCtiExtern = true;

            // Update contact info if necessary
            this.sendCallUpdateEvent(call, initiatedElem);
        }
        else {
            /*CRRAINB-5204++
            call.setStatus(CallStatus.DIALING);
            *///CRRAINB-5204--
            this.sendCallUpdateEvent(call);
        }
    }

    /**
     * Handler of ORIGINATED event
     * @private
     * @param {any} originatedElem Originated sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onOriginatedEvent(originatedElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onOriginatedEvent");

        try {
            const call = await this.getCall(originatedElem, false, true);

            const jid = originatedElem.attr("endpointIm");
            const phoneNumber = originatedElem.attr("endpointTel");
            const newConnectionId = originatedElem.attr("newCallId");

            call.setDirection(CallDirection.OUTGOING);

            // PCG has some info
            let remotePartyPhoneNumber = "";
            if (jid || phoneNumber) { remotePartyPhoneNumber = phoneNumber; }
            // Unknown user
            else if (call.contact && call.contact.temp) {
                remotePartyPhoneNumber = call.contact.id;//this is the only current known phoneNumber
            }
            call.setRemoteParty(remotePartyPhoneNumber, jid, call.contact);
            this.logger.debug("remoteParty: " + call.remoteParty.rawPhoneNumber + " jid " + jid + " " + call.remoteParty.phoneNumber + " " + call.remoteParty.contact.id);

            // Called party identity
            this.telephonyService.setCalledPartyFromEvent(originatedElem, call, phoneNumber, jid);

            this.logger.info(`[TelephonyHybridEventHandler] onOriginatedEvent id '${call.id}' direction '${call.direction}' endpointTel '${phoneNumber}' remotePartyPhoneNumber '${remotePartyPhoneNumber}'`);

            if (newConnectionId !== undefined) {

                const newCall = await this.getOrCreateCall(newConnectionId, jid, phoneNumber);

                this.logger.info("[TelephonyHybridEventHandler] onOriginatedEvent : changed call id from " + call.id + " to " + newConnectionId);

                // compute relevantEqtId and isSecondary properties of new call
                this.computeCallRelevancy(originatedElem, newCall);

                // copy status from old call to new call
                newCall.setStatus(call.callStatus);
                // copy contact from old call to new call
                newCall.setContact(call.contact);
                // set remote party in new call
                newCall.setRemoteParty(remotePartyPhoneNumber, jid, call.contact);
                // copy isOwner from old call to new call
                newCall.isOwner = call.isOwner;
                newCall.setDirection(CallDirection.OUTGOING);
                newCall.redirectionCause = call.redirectionCause;
                newCall.calledParty = call.calledParty;

                if (call.isInExternalWindow) {
                    this.eventService.publish("ON_REPLACE_CALLS_IN_EXTERNAL_POPOUT", newCall, call);
                }

                // Media Pillar ++: swap callref in media pillar context
                if (call.isMediaPillarCall()) {
                    this.eventService.publish("ON_REPLACE_CALLREFS_REQUEST", newCall, call);
                } // Media Pillar --

                // remove old call
                this.telephonyService.clearCall(call);
                this.sendCallUpdateEvent(call);
                this.sendCallUpdateEvent(newCall);
                delete this.telephonyService.calls[call.id];
            } else {
                this.computeCallRelevancy(originatedElem, call);
                if (call.contact.temp) {
                    // update call contact with event data if the previous call contact was temp
                    const contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
                    call.contact = contact;
                }
                call.setStatus(CallStatus.DIALING);
                this.sendCallUpdateEvent(call);
            }
        }
        catch (error) {
            const errorMessage = "onOriginatedEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of DELIVERED event
     * @private
     * @param {any} deliveredElem Delivered sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onDeliveredEvent(deliveredElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onDeliveredEvent");

        try {
            // Preprocessing of specific MAIN Delivered event in case of supervision pickup on multiset supervisor
            if (this.handleComputerMultiDeviceAutoOffHookResource(deliveredElem)) {
                this.logger.info("[TelephonyHybridEventHandler] onDelivered exit after preprocessing of Main evt with supervision pickup resource");
                return;
            }

            // Normal processing
            const call = await this.getCall(deliveredElem, undefined, undefined, undefined, true); // true to search for OPE reservation calls

            this.computeCallRelevancy(deliveredElem, call);

            const type = deliveredElem.attr("type");
            const jid = deliveredElem.attr("endpointIm");
            const phoneNumber = deliveredElem.attr("endpointTel");
            const cause = deliveredElem.attr("cause");
            const correlatorData = deliveredElem.attr('correlatorData') || "";
            const correlatorDataHex = deliveredElem.attr('correlatorDataHex') || "";
            const globalCallId = deliveredElem.attr('globalCallId') || "";
            const resource = deliveredElem.attr('resource') || "";
            const ccdGlobalWaitingTime = deliveredElem.attr('ccdGlobalWaitingTime') || "";

            call.setStatus(type === "outgoing" ? CallStatus.RINGING_OUTGOING : CallStatus.RINGING_INCOMMING);
            call.lastCause = cause ? cause : "";

            call.correlatorData = correlatorData;
            call.correlatorDataHex = correlatorDataHex;
            call.globalCallId = globalCallId;
            call.ccdGlobalWaitingTime = ccdGlobalWaitingTime;

            if (type === "incoming") {
                if (resource) {
                    call.setAutoOffHookResource(resource);
                    this.logger.info("[TelephonyHybridEventHandler] onDelivered autoOffHookResource = " + resource + " callId = " + call.id);
                } else {
                    this.retrieveComputerMultiDeviceAutoOffHookResource(deliveredElem, call);
                }
            }

            this.logger.info(`[TelephonyHybridEventHandler] onDeliveredEvent id '${call.id}' direction '${call.direction}' endpointTel '${phoneNumber}' callerNumber '${call.callerNumber}' calledNumber '${call.calledParty?.phoneNumber}'`);

            // Update contact info if necessary
            await this.updateCallContact(jid, phoneNumber, deliveredElem, call);

            // Compute direction, caller number and called number
            call.setDirection(type as CallDirection);
            // Caller party identity
            call.callerNumber = (type === "outgoing" ? this.contactService.userContact.phoneInternalNumber : phoneNumber);
            // Called party identity
            this.telephonyService.setCalledPartyFromEvent(deliveredElem, call, phoneNumber, jid);

            //autoanswer SFU conference call for "join by phone to web conference" feature
            if (this.telephonyService.autoOffHookConference?.number && call.callerNumber && !call.isSecondary) {
                if (call.callerNumber.indexOf(this.telephonyService.autoOffHookConference.number) !== -1 || this.telephonyService.autoOffHookConference.number.indexOf(call.callerNumber) !== -1) {
                    this.logger.info(`[TelephonyHybridEventHandler] onDeliveredEvent id '${call.id}' autoOffHookConference number '${this.telephonyService.autoOffHookConference.number}' caller '${call.callerNumber}' => auto answerCall`);
                    call.webConferenceRoom = this.telephonyService.autoOffHookConference.room;
                    this.telephonyService.answerCall(call);
                    this.telephonyService.autoOffHookConference = null;
                    return; // return to not publish any event that would display the incoming call popup
                }
            }

            // SFU conference call in deskphone mode or 'other phone' nomadic mode:
            // Set webConferenceRoom in call to be able to hide it in telephonyUI.service
            if (this.telephonyService.webConferenceInvitDeskphoneRoom
                && call.callerNumber && call.callerNumber.length) {
                if (!call.isSecondary
                    || /* call on main device */ !(this.mainService.inject(WEBRTC_GATEWAY_SVC).isMediaPillarCallSituation()) /* 'other phone' nomadic mode */) {
                    const room = this.roomService.getBubbleFromCache(this.telephonyService.webConferenceInvitDeskphoneRoom.jid);
                    this.logger.info(`[TelephonyHybridEventHandler] onDeliveredEvent id '${call.id}' webConferenceInvitDeskphoneRoom from room jid '${this.telephonyService.webConferenceInvitDeskphoneRoom.jid}' name '${this.telephonyService.webConferenceInvitDeskphoneRoom.name}': room data${room ? "" : " not"} ready`);
                    call.webConferenceRoom = room ? room : this.telephonyService.webConferenceInvitDeskphoneRoom;
                    call.contact.updateName("", call.webConferenceRoom.name);
                    if (call.webConferenceRoom.avatar) { call.contact.avatarSrc = call.webConferenceRoom.avatar; }
                    this.telephonyService.webConferenceInvitDeskphoneRoom = null;
                }
            }

            // send CALL_UPDATED event
            this.sendCallUpdateEvent(call, deliveredElem);
        }
        catch (error) {
            const errorMessage = "onDeliveredEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of QUEUED event
     * @private
     * @param {any} queuedElem Queued sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onQueuedEvent(queuedElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onQueuedEvent");

        try {
            // Preprocessing of specific MAIN Queued event in case of supervision pickup on multiset supervisor
            if (this.handleComputerMultiDeviceAutoOffHookResource(queuedElem)) {
                this.logger.info("[TelephonyHybridEventHandler] onQueuedEvent exit after preprocessing of Main evt with supervision pickup resource");
                return;
            }

            // Normal processing
            const type = queuedElem.attr("type");
            const cause = queuedElem.attr("cause");
            const correlatorData = queuedElem.attr('correlatorData') || "";
            const correlatorDataHex = queuedElem.attr('correlatorDataHex') || "";
            const globalCallId = queuedElem.attr('globalCallId') || "";
            const resource = queuedElem.attr('resource') || "";

            // Ignore Queued event for parked calls
            if (cause === "PARK") {
                this.logger.warn("[TelephonyHybridEventHandler] onQueuedEvent -> ignore PARK cause");
                return;
            }

            // Ignore Queued event for oxe reservation by OPE on idle user
            if (type === "incoming" && cause !== "CAMPON" && this.telephonyService.isOxe) {
                // OXE reservation by OPE usecase
                // Note: cause may be "NEWCALL" (for multiline device) or "" (for monoline device) for an Oxe reservation by OPE
                // We create a specific reservation call for this QUEUED "reservation" event if the user is idle (no current calls): this reservation is a private data in the handler ;
                // we don't insert the call in the telephonyservice calls list, so that it does not show any popup;
                // but if the user has already an active call, we consider this QUEUED "reservation" event as a normal QUEUED event,
                // which will create a call in the list of telephonyservice calls, so that the incoming queued call popup will be displayed
                this.logger.info("[TelephonyHybridEventHandler] onQueuedEvent - Oxe Reservation by OPE - cause " + cause);
                if (this.telephonyService.isUserIdle()) {
                    this.logger.warn("[TelephonyHybridEventHandler] onQueuedEvent - Oxe Reservation by OPE - user idle (no active call) -> create reservation call - cause " + cause);
                    await this.createReservationCall(queuedElem);
                    return;
                }
            }

            const call = await this.getCall(queuedElem);

            this.computeCallRelevancy(queuedElem, call);
            const jid = queuedElem.attr("endpointIm");
            const phoneNumber = queuedElem.attr("endpointTel");

            const status = (type === "outgoing") ? CallStatus.QUEUED_OUTGOING : CallStatus.QUEUED_INCOMMING;
            call.setStatus(status);
            call.relevantEquipmentId = CallRB.getDeviceIdFromConnectionId(call.connectionId);

            call.correlatorData = correlatorData;
            call.correlatorDataHex = correlatorDataHex;
            call.globalCallId = globalCallId;

            this.logger.info(`[TelephonyHybridEventHandler] onQueuedEvent id '${call.id}' direction '${call.direction}' endpointTel '${phoneNumber}' callerNumber '${call.callerNumber}' => calledNumber '${call.calledParty?.phoneNumber}'`);

            if (type === "incoming" && (this.telephonyService.isOxe || this.telephonyService.oxoMultiCallMode())) {
                if (resource) {
                    // A call pickup has been engaged on our user, while there was already a call:
                    // If it's engaged by our client, and we are in computer mode, we must answer to this call (and not display the incoming call popup)
                    // Note that it's not possible to do autoanswer with old Oxo ('non multicall') systems
                    // (because the webrtc answercall could answer to a previous incoming ringing call)
                    call.setAutoOffHookResource(resource);
                    this.logger.info("[TelephonyHybridEventHandler] onQueuedEvent autoOffHookResource = " + resource + " callId = " + call.id);
                } else {
                    this.retrieveComputerMultiDeviceAutoOffHookResource(queuedElem, call);
                }
                if (call.autoOffHookResource && call.autoOffHookResource === this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid) && !this.telephonyService.nomadicObject.makeCallInitiatorIsMain) {
                    const webrtcGatewayService = this.mainService.inject(WEBRTC_GATEWAY_SVC);
                    if (!webrtcGatewayService) {
                        throw new Error("TelephonyHybridEventHandler failure - webrtc Gateway service is missing");
                    }
                    if (webrtcGatewayService.isMediaPillarUserSelected()) {
                        this.logger.info("[TelephonyHybridEventHandler] onQueuedEvent | blindPickup in progress in computer mode: let's answer to call " + call.id);
                        call.setAutoOffHookInProgress(true);
                        webrtcGatewayService.answerCall(call);
                    }
                } else {
                    if (call.autoOffHookResource) {
                        this.logger.debug("[TelephonyHybridEventHandler] onQueuedEvent | blindPickup in progress on another client of our user, or Office mode: do nothing for call " + call.id);
                    }
                }
            }

            // Update contact info if necessary
            await this.updateCallContact(jid, phoneNumber, queuedElem, call);

            // Compute direction, caller number and called number
            call.setDirection(type as CallDirection);
            // Caller party identity
            call.callerNumber = (type === "outgoing" ? this.contactService.userContact.phoneInternalNumber : phoneNumber);
            // Called party identity
            this.telephonyService.setCalledPartyFromEvent(queuedElem, call, phoneNumber, jid);

            // send CALL_UPDATED event
            this.sendCallUpdateEvent(call, queuedElem);
        }
        catch (error) {
            const errorMessage = "onQueuedEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    private async createReservationCall(queuedElem: any): Promise<CallRB> {
        const jid = queuedElem.attr("endpointIm");
        const phoneNumber = queuedElem.attr("endpointTel");
        const contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
        const connectionId = queuedElem.attr("callId");
        const call: CallRB = CallRB.create(CallStatus.QUEUED_INCOMMING, connectionId, CallType.PHONE, contact);
        call.setStatus(CallStatus.QUEUED_INCOMMING); // in order to manage duration
        call.setConnectionId(connectionId);
        this.reservationCalls[call.id] = call;
        return call;
    }

    private findReservationCall(callId: string): CallRB {
        return (this.reservationCalls[callId] as CallRB);
    }

    private handleComputerMultiDeviceAutoOffHookResource(deliveredOrQueuedElem: any): boolean {
        try {
            const resource: string = deliveredOrQueuedElem.attr('resource') || "";
            let evtDeviceType: string = deliveredOrQueuedElem.attr("deviceType");
            if (!evtDeviceType) { evtDeviceType = "MAIN"; }
            const cfgNomadic = this.telephonyService.getNomadicObject();
            const cfgOfficePhone: boolean = cfgNomadic ? (cfgNomadic.makeCallInitiatorIsMain || !cfgNomadic.modeActivated) : true;

            if ((evtDeviceType === "MAIN") && !cfgOfficePhone && resource) {
                const type: string = deliveredOrQueuedElem.attr("type");
                try {
                    if (type === "incoming") {
                        // This is the main Delivered/Queued event for a call which must be answered automatically by supervisor to complete supervision pickup:
                        // The auto answer operation will be triggered by delivered or queued event on secondary device, identified by autoanswerCallId
                        // ( endpointIm or endpointTel for oxe / real callid for oxo)

                        // Note : Unfortunately, we can not use the globalCallId on Oxe because if the supervisor is already in established call before pickup ,
                        // the queued event on secondary device for pickup does not contain any globalCallId:
                        // that's why we use endpointIm or endpointTel as correlator between main and secondary event on Oxe

                        // Let's save the autoAnswerCallId which will be used to identify the call on secondary device to complete autoanswer
                        if (this.telephonyService.isOxe) {
                            const endpointImOrTel: string = deliveredOrQueuedElem.attr('endpointIm') || deliveredOrQueuedElem.attr('endpointTel') || "";
                            if (endpointImOrTel) {
                                this.logger.info("[TelephonyHybridEventHandler] handleComputerMultiDeviceAutoOffHookResource store main evt resource '" + resource + "' for  endpointImOrTel = " + endpointImOrTel);
                                this.telephonyService.setAutoAnswerCorrelatorIdResourceMap(endpointImOrTel, resource);
                            }
                        } else if (this.telephonyService.isOxo) {
                            const callId: string = deliveredOrQueuedElem.attr("callId");
                            const realCallId: string = CallRB.getCallIdFromConnectionId(callId);
                            if (realCallId) {
                                // In Oxo , we must check if a call already exists with this call Id because, if the supervisor is already in established call before pickup,
                                // the secondary queued event for the deflect call will occur before main queued event => we must manage both cases

                                // Check if one call exists with this real callId
                                const foundConnectionId = Object.keys(this.telephonyService.calls || []).find((callConnectionId) => {
                                    return CallRB.getCallIdFromConnectionId(callConnectionId) === realCallId;
                                });
                                if (foundConnectionId) {
                                    // The call already exists (secondary event has occured before main event): try to answer it
                                    const foundCall: CallRB = this.telephonyService.calls[foundConnectionId];
                                    const webrtcGatewayService = this.mainService.inject(WEBRTC_GATEWAY_SVC);
                                    if (!webrtcGatewayService) {
                                        throw new Error("TelephonyHybridEventHandler failure - webrtc Gateway service is missing");
                                    }
                                    if (foundCall && (foundCall.status === CallRB.Status.RINGING_INCOMMING || foundCall.status === CallRB.Status.QUEUED_INCOMMING)) {
                                        foundCall.setAutoOffHookResource(resource);

                                        if (webrtcGatewayService.getMediaPillarCallContext(foundCall)?.webrtcCallRef) {
                                            foundCall.setAutoOffHookInProgress(true);
                                            webrtcGatewayService.answerCall(foundCall);
                                        }
                                    }
                                } else {
                                    // The call doesn't exist yet (main event has occured before secondary event): store autoanswer resource for this call
                                    this.logger.info("[TelephonyHybridEventHandler] handleComputerMultiDeviceAutoOffHookResource store main evt resource '" + resource + "' for  realCallId = " + realCallId);
                                    this.telephonyService.setAutoAnswerCorrelatorIdResourceMap(realCallId, resource);
                                }
                            }
                        }
                    }
                } catch (error) {
                    this.logger.error("[TelephonyHybridEventHandler] handleComputerMultiDeviceAutoOffHookResource Evt main & !office: Error: " + error?.message);
                }
                return true; // return true means no further processing must be done on this event
            }
        } catch (error) {
            this.logger.error("[TelephonyHybridEventHandler] handleComputerMultiDeviceAutoOffHookResource Error: " + error?.message);
        }
        return false;
    }

    private retrieveComputerMultiDeviceAutoOffHookResource(deliveredOrQueuedElem: any, call: CallRB): void {
        try {
            const resource: string = deliveredOrQueuedElem.attr('resource') || "";
            let evtDeviceType: string = deliveredOrQueuedElem.attr("deviceType");
            if (!evtDeviceType) { evtDeviceType = "MAIN"; }
            const cfgNomadic = this.telephonyService.getNomadicObject();
            const cfgOfficePhone: boolean = cfgNomadic ? (cfgNomadic.makeCallInitiatorIsMain || !cfgNomadic.modeActivated) : true;

            this.logger.debug(`[TelephonyHybridEventHandler] retrieveComputerMultiDeviceAutoOffHookResource evtDeviceType ${evtDeviceType} cfgOfficePhone ${cfgOfficePhone} resource ${resource}`);

            if ((evtDeviceType === "SECONDARY") && !cfgOfficePhone && !resource) {
                //check if there is a stored resource for this call on corresponding MAIN event
                if (this.telephonyService.isOxe) {
                    // the correlator between main and secondary events is the endpointIm or the endpointTel
                    const endpointIm: string = deliveredOrQueuedElem.attr('endpointIm') || "";
                    const endpointTel: string = deliveredOrQueuedElem.attr('endpointTel') || "";
                    this.logger.debug(`[TelephonyHybridEventHandler] retrieveComputerMultiDeviceAutoOffHookResource oxe endpointIm ${endpointIm} endpointTel ${endpointTel}`);

                    if (endpointIm && this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(endpointIm)) {
                        this.logger.info("[TelephonyHybridEventHandler] retrieveComputerMultiDeviceAutoOffHookResource retrieve stored resource '" + this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(endpointIm) + "' for  endpointIm = " + endpointIm);
                        call.setAutoOffHookResource(this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(endpointIm));
                        this.telephonyService.removeAutoAnswerCorrelatorId(endpointIm);
                    }
                    if (endpointTel && this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(endpointTel)) {
                        this.logger.info("[TelephonyHybridEventHandler] retrieveComputerMultiDeviceAutoOffHookResource retrieve stored resource '" + this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(endpointTel) + "' for  endpointTel = " + endpointTel);
                        call.setAutoOffHookResource(this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(endpointTel));
                        this.telephonyService.removeAutoAnswerCorrelatorId(endpointTel);
                    }
                } else if (this.telephonyService.isOxo) {
                    // the correlator between main and secondary events is the real csta callId
                    const callId: string = deliveredOrQueuedElem.attr("callId");
                    const realCallId: string = CallRB.getCallIdFromConnectionId(callId);
                    this.logger.debug(`[TelephonyHybridEventHandler] retrieveComputerMultiDeviceAutoOffHookResource oxo callId ${callId} realCallId ${realCallId}`);

                    if (realCallId && this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(realCallId)) {
                        this.logger.info("[TelephonyHybridEventHandler] retrieveComputerMultiDeviceAutoOffHookResource retrieve stored resource '" + this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(realCallId) + "' for  realCallId = " + realCallId);
                        call.setAutoOffHookResource(this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(realCallId));
                        this.telephonyService.removeAutoAnswerCorrelatorId(realCallId);
                    }
                }
            }
        } catch (error) {
            this.logger.error("[TelephonyHybridEventHandler] retrieveComputerMultiDeviceAutoOffHookResource Error: " + error?.message);
        }
    }

    /**
     * Handler of FAILED event
     * @private
     * @param {any} failedElem Failed sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onFailCallEvent(failedElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onFailCallEvent");

        try {
            const cause = failedElem.attr("cause");
            const call = await this.getCall(failedElem);

            call.setStatus(CallStatus.ERROR);
            call.errorMessage = TelephonyHybridEventHandler.CallFailureLabels[cause];
            if (!call.errorMessage) { call.errorMessage = cause; }
            this.sendCallUpdateEvent(call, failedElem);
        }
        catch (error) {
            const errorMessage = "onFailCallEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of UPDATECALL event
     * @private
     * @param {any} updatecallElem Updatecall sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onUpdateCallEvent(updatecallElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onUpdateCallEvent");

        try {
            let opeReservationCall: boolean = false;

            let call = await this.getCall(updatecallElem, undefined, undefined, true); // true to prevent call creation
            if (!call) {
                // check wether one call for OPE reservation (oxe) exists with this event callId
                call = this.findReservationCall(updatecallElem.attr("callId"));
                if (!call) {
                    this.logger.info("[TelephonyHybridEventHandler] onUpDateCallEvent call does exist => IGNORED event");
                    return;
                }
                this.logger.info("[TelephonyHybridEventHandler] onUpDateCallEvent for OPE reservation call " + call.id);
                opeReservationCall = true;
            }
            if (call.webConferenceRoom) {
                // Ignore event for phone call in web conference in order to avoid unwanted call popup
                this.logger.info("[TelephonyHybridEventHandler] onUpDateCallEvent for pbx call inside webConference " + call.id + " => IGNORED event");
                return;
            }

            const jid = updatecallElem.attr("endpointIm");
            let phoneNumber = updatecallElem.attr("endpointTel");
            let firstName = "";
            let lastName = "";
            const identityFirstName = updatecallElem.find("identity").attr("firstName");
            const identityLastName = updatecallElem.find("identity").attr("lastName");
            const identityDisplayName = updatecallElem.find("identity").attr("displayName");
            const identityPBXDisplayName = updatecallElem.find("identity").attr("pbxDisplayName");
            const origin = updatecallElem.find("identity").attr("origin"); //RQRAINB-10966
            const userId = updatecallElem.find("identity").attr("userId"); //RQRAINB-10966
            const identityRole = updatecallElem.find("identity").attr("role");

            let contactUpdateDone = false;

            if (origin && userId) { //RQRAINB-10966
                this.logger.info("[TelephonyHybridEventHandler] onUpdateCallEvent :  origin/userId = " + origin + " / " + userId);
            }

            if (!config.permitSearchFromPhoneBook) { // <--- allow to permit search even if not the good profile
                //check if phonebook is allowed by profile else no result
                if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_PHONE_BOOK)) {
                    this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent xnames not allowed for the user profile => IGNORED event");
                    return;
                }
            }

            //find Xnames from directories
            if (identityLastName && identityLastName.length) {
                lastName = identityLastName;
            }
            if (identityFirstName && identityFirstName.length) {
                firstName = identityFirstName;
            }

            if (lastName.length > 0 || firstName.length > 0) {
                const foundName: string = lastName + firstName;
                this.logger.info("[TelephonyHybridEventHandler] onUpDateCallEvent received for call " + call.id + " for phoneNumber:" + anonymizer(phoneNumber)
                    + " with name : " + foundName.slice(0, 1) + "***");
            } else {
                if (identityDisplayName && identityDisplayName.length && identityDisplayName !== phoneNumber) {
                    lastName = identityDisplayName; //Workaround last resort, only displayName is available, hack is to use it as lastName
                    this.logger.info("[TelephonyHybridEventHandler] onUpDateCallEvent only displayName available");
                } else {
                    this.logger.info("[TelephonyHybridEventHandler] onUpDateCallEvent xnames not available => IGNORED event");
                    return;
                }
            }

            // update contact as necessary
            if (!jid && !phoneNumber) { phoneNumber = "****"; }

            //RQRAINB-10966++
            let contact: Contact = null;
            if (jid) { //rainbow user
                contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
            } else { //not a rainbow user
                if (userId && origin && origin !== "RAINBOW") { //new enhanced user info
                    try {
                        switch (origin) {
                            case "O365":
                                contact = await this.office365AdService.getO365ContactFromServer(userId);
                                break;
                            case "PERSONAL_DIRECTORY":
                            case "BUSINESS_DIRECTORY":
                                contact = await this.companyDirectoryService.getDirectoryContactById(userId);
                                break;

                            case "OUTLOOK":
                                contact = await this.office365AdService.getOutlookContactById(userId);
                                break;

                            case "PBX":
                                contact = null; //nothing new to be expected from pbx server search then manage it from PCG included info as the case of no contact found
                                break;
                            default:
                                this.logger.error("[TelephonyHybridEventHandler] onUpDateCallEvent anomaly directory origin not managed");
                                contact = null;
                                break;
                        }
                    } catch (error) { //something wrong with directory search
                        this.logger.error("[TelephonyHybridEventHandler] onUpDateCallEvent anomaly directory search error : " + error.status);
                        contact = null;
                    }
                    if (!contact) { contact = await this.contactService.getOrCreateContact(jid, phoneNumber); }
                    this.logger.info("[TelephonyHybridEventHandler] onUpdateCallEvent :  origin//contactId = " + contact.id);
                } else { // if no directory info basic temp contact managed as former way
                    contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
                }
            }
            //RQRAINB-10966--

            if (contact.temp || (contact.o365Flag || contact.outlookFlag || contact.companyDirectoryFlag || contact.personalDirectoryFlag)) {
                //not a rainbow user: temp contact or directory contact
                if (contact.temp) {
                    // Do not modify a directory contact
                    contact.updateName(firstName, lastName);
                }
                if (call.contact && call.contact.id) {	//not a conf
                    if (call.contact.id !== contact.id || call.contact.displayName === phoneNumber || call.contact.nameUpdatePrio === Contact.NameUpdatePrio.OUTLOOK_UPDATE_PRIO) {
                        if (contact.temp) {
                            // Do not modify a directory contact
                            contact.nameUpdatePrio = Contact.NameUpdatePrio.SERVER_UPDATE_PRIO;//flag as server prio
                        }
                        call.setContact(contact);
                        call.setRemotePartyContact(contact, jid);
                        this.logger.debug("remoteParty: " + call.remoteParty.rawPhoneNumber + " " + call.remoteParty.phoneNumber + " " + call.remoteParty.contact.id);
                        contactUpdateDone = true;
                        this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent xnames updated for " + phoneNumber + " with contact : " + contact.nameForLogs);

                    }
                } else if (call.participants && call.participants.length > 0) {
                    // conference call
                    for (let i = 0; i < call.participants.length; i++) {
                        if (call.participants[i].temp) {

                            if (call.participants[i].phoneProCan && call.participants[i].phoneProCan === phoneNumber) { //concerned participant
                                this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent temp participant "
                                    + call.participants[i].nameForLog + " updated with : " + contact.nameForLogs);
                                call.participants[i] = contact;
                                if (contact.temp) {
                                    // Do not modify a directory contact
                                    call.participants[i].nameUpdatePrio = Contact.NameUpdatePrio.SERVER_UPDATE_PRIO;//flag as server prio
                                }
                                call.remoteParties[i].contact = contact;
                                contactUpdateDone = true;
                            }
                        }
                        else { //former participant is a rainbow user I don't know what to do !!!???
                            this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent STRANGE former participant was a rainbow: " + call.participants[i].nameForLog);
                        }
                    }
                }
            } else { //rainbow contact found
                if (call.contact && call.contact.id) {	//not a conf
                    if (call.contact.id !== contact.id) { //update call.contact if not the good one
                        //workaround+ Because of msg crossing pb udate old temp contact before to set the new one
                        if (call.contact.temp) {
                            call.contact.updateName(firstName, lastName);
                            // call.contact.nameUpdatePrio = Contact.NameUpdatePrio.SERVER_UPDATE_PRIO;
                        }
                        //workaround-
                        call.setContact(contact);
                        call.setRemotePartyContact(contact, jid); // no need to update phonenumber on updateCall event
                        this.logger.debug("remoteParty: " + call.remoteParty.rawPhoneNumber + " " + call.remoteParty.phoneNumber + " " + call.remoteParty.contact.id);
                        contactUpdateDone = true;
                        this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent call update with rainbow contact : " + contact.nameForLogs);
                    }
                } else if (call.participants && call.participants.length > 0) { //conference
                    for (let i = 0; i < call.participants.length; i++) {
                        if (call.participants[i].temp) {
                            // former participant was not a rainbow user
                            if (call.participants[i].phoneProCan && call.participants[i].phoneProCan === phoneNumber) { //concerned participant
                                this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent temp participant "
                                    + call.participants[i].nameForLog + " updated with : " + contact.nameForLogs);
                                call.participants[i] = contact;
                                call.setParticipants(call.participants);//to force an update of the avatars
                                call.remoteParties[i].contact = contact;
                                call.remoteParties[i].jid = jid;
                                contactUpdateDone = true;
                            }
                        }
                        else { //if former participant is the same rainbow user nothing to do except store phoneNumber and participant in call
                            if (call.participants[i].jid === jid) { //concerned participant
                                // contactUpdateDone = true;
                                this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent rainbow participant "
                                    + call.participants[i].nameForLog + " same contact jid : do nothing : " + contact.nameForLogs);
                            }
                            else { //not the good participant nothing to do
                                this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent other participant not updated : "
                                    + call.participants[i].nameForLog + " vs " + contact.nameForLogs);
                            }
                        }
                    }
                }
            }

            if (identityPBXDisplayName) {
                call.PBXDisplay = identityPBXDisplayName;
                call.role = identityRole;
                contactUpdateDone = true;
            }

            if (contactUpdateDone && !opeReservationCall) {
                this.sendCallUpdateEvent(call, updatecallElem);
            }
            else {
                this.logger.debug("[TelephonyHybridEventHandler] onUpDateCallEvent, no update needed for call or OPE reservation call : " + call.id);
            }

        }
        catch (error) {
            const errorMessage = "onUpdateCallEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of UPDATEPARTY event
     * @private
     * @param {any} updatepartyElem Updateparty sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */

    private async onUpdatePartyEvent(updatePartyElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onUpdatePartyEvent");
        try {
            const call = await this.getCall(updatePartyElem, undefined, undefined, true); // true to prevent call creation
            if (!call) {
                this.logger.info("[TelephonyHybridEventHandler] onUpDatePartyEvent call does exist => IGNORED event");
                return;
            }

            const callUpdated: boolean = await this.telephonyService.setCalledPartyFromEvent(updatePartyElem, call);
            if (callUpdated) {
                this.sendCallUpdateEvent(call, updatePartyElem);
            }
        }
        catch (error) {
            const errorMessage = "onUpdateCallEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of ESTABLISHED event
     * @private
     * @param {any} establishedElem Established sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onEstablishedEvent(establishedElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onEstablishedEvent");

        try {
            const call: CallRB = await this.getCall(establishedElem);

            this.computeCallRelevancy(establishedElem, call);

            const jid = establishedElem.attr("endpointIm");
            let phoneNumber = establishedElem.attr("endpointTel");
            const connectionId = establishedElem.attr("callId");
            const globalCallId = establishedElem.attr('globalCallId') || "";

            if (this.settingsService.getBooleanSetting("openPopoutOnAnswerCall") && this.telephonyService.isOxe) {
                call.isInExternalWindow = true;
            }

            // Call already exists and IS NOT a conference, update contact info if necessary
            if (call.contact?.id) {
                call.setStatus(CallStatus.ACTIVE);
                call.setConnectionId(connectionId);
                call.globalCallId = globalCallId;
                if (!call.direction && this.telephonyService.isOxe) {
                    this.computeCallDirectionFromGlobalCallId(call);
                }
                if (call.direction === CallDirection.UNDEFINED) { call.setDirection(CallDirection.INCOMING); }
                call.callerNumber = (call.direction === CallDirection.OUTGOING ? this.contactService.userContact.phoneInternalNumber : phoneNumber);
                this.telephonyService.setCalledPartyFromEvent(establishedElem, call, phoneNumber, jid);

                this.logger.info(`[TelephonyHybridEventHandler] onEstablishedEvent id '${call.id}' direction '${call.direction}' endpointTel '${phoneNumber}' callerNumber '${call.callerNumber}' calledNumber '${call.calledParty?.phoneNumber}'`);

                // Update contact info as necessary
                await this.updateCallContact(jid, phoneNumber, establishedElem, call);

                if (call.webConferenceRoom) {
                    // if the call is for a room, use the room name and avatar
                    call.contact.updateName("", call.webConferenceRoom.name);
                    if (call.webConferenceRoom.jid && !call.webConferenceRoom.dbId) {
                        //try to retrieve full room data
                        const room = this.roomService.getBubbleFromCache(call.webConferenceRoom.jid);
                        if (room) { call.webConferenceRoom = room; }
                    }
                    if (call.webConferenceRoom.avatar) { call.contact.avatarSrc = call.webConferenceRoom.avatar; }
                }

                // send CALL_UPDATED event
                this.sendCallUpdateEvent(call, establishedElem);
            }
            else if (call.participants && call.participants.length > 0) {
                // The call was a conference and becomes ESTABLISHED: the conference has been ended
                // Recover former matching contact from participants
                let contactRecovered: Contact = null;
                let confParticipantOldDirection: CallDirection = null;
                for (let i = 0; (i < call.participants.length && !contactRecovered); i++) {
                    if (call.participants[i].id === jid) {
                        contactRecovered = call.participants[i];
                        confParticipantOldDirection = call.remoteParties[i].direction;
                    }
                    else if (call.remoteParties
                        && call.remoteParties.length > 0
                        && call.remoteParties[i].rawPhoneNumber === phoneNumber) {
                        contactRecovered = call.participants[i];
                        confParticipantOldDirection = call.remoteParties[i].direction;
                    }
                }
                //clean former conf struct & update contact
                call.participants = [];
                call.isConference = false;
                if (contactRecovered) {
                    call.setContact(contactRecovered);
                    call.setStatus(CallStatus.ACTIVE);
                    call.setDirection(confParticipantOldDirection);
                    const computedRemotePartyNumber = this.computeRemotePartyNumber(jid, phoneNumber);
                    call.setRemoteParty(phoneNumber, jid, contactRecovered, computedRemotePartyNumber);
                    this.logger.info(`[TelephonyHybridEventHandler] onEstablishedEvent id '${call.id}' direction '${call.direction}' endpointTel '${phoneNumber}' contact recovered from previous conf contact`);
                    this.logger.debug("remoteParty: " + call.remoteParty.rawPhoneNumber + " " + call.remoteParty.phoneNumber + " " + call.remoteParty.contact.id);
                    this.sendCallUpdateEvent(call, establishedElem);
                }
                else { // no matching contact strange but go ahead ...
                    if (!jid && !phoneNumber) { phoneNumber = "****"; }

                    const contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
                    call.setContact(contact);
                    call.setStatus(CallStatus.ACTIVE);
                    call.setRemoteParty(phoneNumber, jid, contact);
                    this.logger.info(`[TelephonyHybridEventHandler] onEstablishedEvent id '${call.id}' direction '${call.direction}' endpointTel '${phoneNumber}' contact not recovered from previous conf contact: new contact created`);
                    this.logger.debug("remoteParty: " + call.remoteParty.rawPhoneNumber + " " + call.remoteParty.phoneNumber + " " + call.remoteParty.contact.id);
                    this.sendCallUpdateEvent(call, establishedElem);
                }
            } else { //strange situation, no call contact and not a conf with call participants
                //CRRAINB-4392
                this.logger.info("[TelephonyHybridEventHandler] onEstablishedEvent, no call contact / no participants");
                if (!jid && !phoneNumber) { phoneNumber = "****"; }

                const contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
                call.setContact(contact);
                call.setStatus(CallStatus.ACTIVE);
                call.setRemoteParty(phoneNumber, jid, contact);
                this.logger.debug("remoteParty: " + call.remoteParty.rawPhoneNumber + " " + call.remoteParty.phoneNumber + " " + call.remoteParty.contact.id);
                this.sendCallUpdateEvent(call, establishedElem);
            }
        }
        catch (error) {
            const errorMessage = "onEstablishedEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    private computeRemotePartyNumber(jid: string, phoneNumber: string): string {
        this.logger.info("[TelephonyHybridEventHandler] computeRemotePartyNumber jid '" + jid + "' phoneNumber '" + anonymizer(phoneNumber) + "'");

        if (jid) {
            const contact: Contact = this.contactService.getContactFromCacheByJid(jid);
            if (contact) {
                // check if the phoneNumber is one of the contact phone numbers
                if (contact.phoneInternalNumber === phoneNumber
                    || contact.phonePro === phoneNumber
                    || contact.phoneProCan === phoneNumber
                    || contact.mobilePro === phoneNumber
                    || contact.mobileProCan === phoneNumber
                    || contact.phonePerso === phoneNumber
                    || contact.phonePersoCan === phoneNumber
                    || contact.mobilePerso === phoneNumber
                    || contact.mobilePersoCan === phoneNumber) {
                    // phonenumber is one of the known user's numbers, we can use it
                    this.logger.info("[TelephonyHybridEventHandler] computeRemotePartyNumber phone Number " + anonymizer(phoneNumber) + " is one of the contact phone numbers: use it as remote party number => " + anonymizer(phoneNumber));
                    return phoneNumber;
                }

                // phonenumber is not one of the known user's numbers (it may be a private number hiden by the user, or a remote extension number),
                // we must not use it or give it to sdk clients: we replace it by one of the known user's numbers (internal or public)
                const remotePartyNumber = contact.phoneInternalNumber || contact.phonePro || phoneNumber;
                this.logger.info("[TelephonyHybridEventHandler] computeRemotePartyNumber phone Number " + anonymizer(phoneNumber) + " is NOT one of the contact phone numbers: replace it by a contact phone Nnumber => " + anonymizer(remotePartyNumber));
                return remotePartyNumber;
            }
        }

        this.logger.info("[TelephonyHybridEventHandler] computeRemotePartyNumber phone Number " + anonymizer(phoneNumber) + " no jid: remote Party Number based on endpointTel => " + anonymizer(phoneNumber));
        return phoneNumber;
    }

    private computeCallDirectionFromGlobalCallId(call: CallRB): void {
        // search for other call with same globalCallId
        const previousCallWithSameGlobalId: CallRB = this.telephonyService.getOtherCallWithSameGlobalCallId(call);
        if (previousCallWithSameGlobalId) {
            call.setDirection(previousCallWithSameGlobalId.direction);
            call.setAutoOffHookInProgress(previousCallWithSameGlobalId.getAutoOffHookInProgress());
            call.calledParty = previousCallWithSameGlobalId.calledParty;
            call.redirectionCause = previousCallWithSameGlobalId.redirectionCause;
            this.logger.info("[TelephonyHybridEventHandler] computeCallDirectionFromGlobalCallId " + call + " => direction: " + call.direction);
        }
    }

    /**
     * Handler of HOLD event
     * @private
     * @param {any} heldElem Held sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onHeldEvent(heldElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onHeldEvent");

        try {
            const call = await this.getCall(heldElem, true);

            if (call.callStatus === CallStatus.ENDED) {
                // Handling of HOLD event with cause RECALL as a ringing incoming call
                // (used for CCD when releaseCall by agent is forbidden by oxe configuration, but is invoked by agent)
                const cause: string = heldElem.attr("cause");
                this.logger.info("[TelephonyHybridEventHandler] onHeldEvent: found ENDED call " + call.id + " cause " + cause);
                if (cause === "RECALL") {
                    let timeout: number = 1;
                    const webrtcGatewayService = this.mainService.inject(WEBRTC_GATEWAY_SVC);
                    if (!webrtcGatewayService) {
                        throw new Error("TelephonyHybridEventHandler failure - webrtc Gateway service is missing");
                    }
                    // we wait 1000 ms if mp context not free (because webrtc call not yet released), before re-activation of ringing incoming call
                    if (webrtcGatewayService.isMediaPillarUserSelected()
                        && webrtcGatewayService.mediaPillarCallContext.callState !== MPCallState.Free) { timeout = 1000; }
                    setTimeout(() => {
                        if (call.callStatus === CallStatus.ENDED) {
                            this.logger.info("[TelephonyHybridEventHandler] onHeldEvent: found ENDED call " + call.id + " cause " + cause + " => reactivating call with state RINGING_INCOMMING");
                            call.setStatus(CallStatus.RINGING_INCOMMING);
                            this.sendCallUpdateEvent(call, heldElem);
                        }
                    }, timeout);
                    return;
                }
                this.logger.warn("[TelephonyHybridEventHandler] onHeldEvent: found call already ended : IGNORE HOLD event for call " + call.id);
                return;
            }

            const connectionId = heldElem.attr("callId");
            const callDeviceId = CallRB.getDeviceIdFromConnectionId(call.connectionId);
            const holdDeviceId = CallRB.getDeviceIdFromConnectionId(connectionId);
            if (callDeviceId === holdDeviceId) { call.setStatus(CallStatus.HOLD); }
            // Same callId but different equipmentId (We receive equipment id of user who put us in held)
            else { call.setStatus(CallStatus.PUT_ON_HOLD); }
            // Don't compute call relevancy here because equipmentId may be the Equipment Id of the other party
            this.sendCallUpdateEvent(call, heldElem);
        }
        catch (error) {
            const errorMessage = "onHeldEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of RETRIEVE event
     * @private
     * @param {any} retrieveElem Retrieve sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onRetrieveCallEvent(retrieveElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onRetrieveCallEvent");

        try {
            const call = await this.getCall(retrieveElem, true);

            call.setStatus(CallStatus.ACTIVE);
            // Don't compute call relevancy here because equipmentId may be the Equipment Id of the other party
            this.sendCallUpdateEvent(call, retrieveElem);
        }
        catch (error) {
            const errorMessage = "onRetrieveCallEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of DIVERTED event
     * @private
     * @param {any} divertedElem Diverted sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private onDivertedEvent(divertedElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onDivertedEvent");

        try {
            const oldConnectionId = divertedElem.attr("oldCallId");
            const oldCallId = CallRB.getIdFromConnectionId(oldConnectionId);
            const call = this.telephonyService.calls[oldCallId];
            if (!call) {
                this.logger.warn("[TelephonyHybridEventHandler] onDivertedEvent - receive divertedEvent on unknown call --- ignored");
                return;
            }
            this.computeCallRelevancy(divertedElem, call); //CRRAINB-3416

            this.telephonyService.clearCall(call, true); //2nd para true for cleacall without broadcast
            this.sendCallUpdateEvent(call, divertedElem);
        }
        catch (error) {
            const errorMessage = "onDivertedEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of TRANSFER event
     * @private
     * @param {any} transferElem Transfer sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onTransferEvent(transferElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onTransferEvent");

        try {
            // Extract transfert call parameters
            const activeConnectionId: string = transferElem.attr("activeCallId");
            const heldConnectionId: string = transferElem.attr("heldCallId");
            const newConnectionId: string = transferElem.attr("newCallId");

            // Get active call
            const activeCallId: string = CallRB.getIdFromConnectionId(activeConnectionId);
            const activeCall: CallRB = this.telephonyService.calls[activeCallId];
            //Note: activeCall may be null (case of blind transfer by operator)

            if (heldConnectionId) {
                // we are the transfer initiator
                // Get the held call
                const heldCallId: string = CallRB.getIdFromConnectionId(heldConnectionId);
                const heldCall: CallRB = this.telephonyService.calls[heldCallId];

                // Release both calls (active and held)
                if (heldCall) {
                    heldCall.setStatus(CallStatus.ENDED);
                }
                if (activeCall) {
                    activeCall.setStatus(CallStatus.ENDED);
                }
                if (heldCall) {
                    this.sendCallUpdateEvent(heldCall);
                }
                if (activeCall) {
                    this.sendCallUpdateEvent(activeCall);
                }
            }

            if (newConnectionId) {
                // we are not the transfer initiator
                const jid: string = transferElem.attr("newEndpointIm");
                let phoneNumber: string = transferElem.attr("newEndpointTel");
                let deviceState: string = transferElem.attr("deviceState");
                if (!deviceState) { deviceState = transferElem.attr("deviceStatus"); }

                let deviceType: string = transferElem.attr("deviceType");
                if (!deviceType) { deviceType = "MAIN"; }

                // remove possible OPE reservation call (if the old call before the transfer was a queued reservation call for Oxe OPE)
                delete this.reservationCalls[activeCallId];

                if (!jid && !phoneNumber) { //secret identity
                    phoneNumber = "****";
                }

                // Release current call
                // Note : this call is released here, before the creation of the new call, to avoid having 2 simultaneauous active calls during a short time
                if (activeCall && activeCall.callStatus !== CallStatus.ENDED) {
                    activeCall.setStatus(CallStatus.ENDED);
                    this.sendCallUpdateEvent(activeCall);
                }

                // Create new call
                const newCall: CallRB = await this.getOrCreateCall(newConnectionId, jid, phoneNumber);

                if (deviceState && deviceState === "LCI_ALERTING") { newCall.setStatus(CallStatus.RINGING_INCOMMING); newCall.setDirection(CallDirection.INCOMING); }
                else if (deviceState && deviceState === "LCI_QUEUED") { newCall.setStatus(CallStatus.QUEUED_INCOMMING); newCall.setDirection(CallDirection.INCOMING); }
                else { newCall.setStatus(CallStatus.ACTIVE); newCall.setDirection(activeCall ? activeCall.direction : CallDirection.UNDEFINED); }

                newCall.relevantEquipmentId = CallRB.getDeviceIdFromConnectionId(newConnectionId);
                newCall.isSecondary = deviceType && deviceType === "SECONDARY";
                newCall.setLastCause("transfer"); // used by desktop custom command

                // compute call direction from previous call direction
                if (activeCall) {
                    newCall.setDirection(activeCall.getDirection());
                    newCall.callerNumber = newCall.direction === CallDirection.OUTGOING ? this.contactService.userContact.phoneInternalNumber : phoneNumber;
                    newCall.transferInitiatorUserId = activeCall.contact?.dbId; // may be null if the remote party is not a rainbow user
                    this.logger.info(`[TelephonyHybridEventHandler] onTransferEvent new Call id '${newCall.id}' direction '${newCall.direction}' endpointTel '${phoneNumber}' callerNumber '${newCall.callerNumber}'`);
                }

                if (activeCall?.isInExternalWindow) {
                    newCall.isInExternalWindow = true;
                }

                // Update contact info if necessary
                await this.updateCallContact(jid, phoneNumber, transferElem, newCall);

                // send CALL_UPDATED event
                this.sendCallUpdateEvent(newCall, transferElem);
            }
        }
        catch (error) {
            const errorMessage = "onTransferEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of CONFERENCE event
     * @private
     * @param {any} conferencedElem Conference sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onConferenceEvent(conferencedElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onConferenceEvent");

        // Get connectionsIds
        const primaryOldConnectionId = conferencedElem.find("primaryOldCallId").text();
        const secondaryOldConnectionId = conferencedElem.find("secondaryOldCallId").text();
        const newConnectionId = conferencedElem.find("newCallId").text();

        // Extract callIds
        const primaryOldCallId = CallRB.getIdFromConnectionId(primaryOldConnectionId);
        const secondaryOldCallId = CallRB.getIdFromConnectionId(secondaryOldConnectionId);

        // Get current calls
        const primaryOldCall: CallRB = this.telephonyService.calls[primaryOldCallId];
        const secondaryOldCall: CallRB = this.telephonyService.calls[secondaryOldCallId];

        // Prepare participant promises
        const participantPromises: Promise<void>[] = [];
        const confParticipants: { contact: Contact; phoneNumber: string; jid: string; direction: string; }[] = [];

        if (newConnectionId && (primaryOldConnectionId === newConnectionId)) {
            this.logger.info("[TelephonyHybridEventHandler] onConferenceEvent intrude usecase : IGNORED");
            return;
        }

        conferencedElem.find("participant").each((__index: number, participant: Element) => {
            const participantElem = $(participant);
            let endpointTel = participantElem.find("endpointTel").text();
            const endpointIm = participantElem.find("endpointIm").text();

            if (!(endpointIm && this.contactService.isUserContactJid(endpointIm))) {
                // This participant is not myself
                participantPromises.push((async (): Promise<void> => {

                    if (!endpointIm && !endpointTel) { endpointTel = "****"; }

                    // try to find contact info out of primary or secondary calls by jid or phone number
                    if (primaryOldCall && primaryOldCall.contact
                        && primaryOldCall.remoteParty
                        && ((endpointIm && primaryOldCall.remoteParty.jid === endpointIm)
                            || primaryOldCall.remoteParty.rawPhoneNumber === endpointTel || primaryOldCall.remoteParty.phoneNumber === endpointTel)) {
                        this.logger.debug("[TelephonyHybridEventHandler] onConferenceEvent found participant " + endpointTel + " from primaryOldCall " + primaryOldCall + " direction " + primaryOldCall.direction);
                        confParticipants.push({ contact: primaryOldCall.contact, phoneNumber: endpointTel, jid: endpointIm, direction: primaryOldCall.direction });
                    }
                    else if (secondaryOldCall && secondaryOldCall.contact
                        && secondaryOldCall.remoteParty
                        && ((endpointIm && secondaryOldCall.remoteParty.jid === endpointIm)
                            || secondaryOldCall.remoteParty.rawPhoneNumber === endpointTel || secondaryOldCall.remoteParty.phoneNumber === endpointTel)) {
                        this.logger.debug("[TelephonyHybridEventHandler] onConferenceEvent found participant " + endpointTel + " from secondaryOldCall " + secondaryOldCall + " direction " + secondaryOldCall.direction);
                        confParticipants.push({ contact: secondaryOldCall.contact, phoneNumber: endpointTel, jid: endpointIm, direction: secondaryOldCall.direction });
                    }
                    else {
                        try {
                            this.logger.debug("[TelephonyHybridEventHandler] onConferenceEvent participant " + endpointTel + " not found in old calls : create new contact " + endpointTel + " " + endpointIm);
                            const contact = await this.contactService.getOrCreateContact(endpointIm, endpointTel);

                            confParticipants.push({ contact: contact, phoneNumber: endpointTel, jid: endpointIm, direction: "" });

                            //manage Outlook Call Party identification
                            try {
                                const updateStatus = await this.telephonyService.updateContactFromOutlookInfos(contact, endpointTel);
                                if (updateStatus) {
                                    this.logger.debug("[TelephonyHybridEventHandler] onConferenceEvent, update from outlook for contact :" + contact.nameForLogs);
                                    //this.makeUpdateContact(call, contact, phoneNumber, actionElemName);
                                } else {
                                    this.logger.debug("[TelephonyHybridEventHandler] onConferenceEvent, no update from outlook for contact :" + contact.nameForLogs);
                                }
                            }
                            catch (error) {
                                this.logger.debug("[TelephonyHybridEventHandler] on conferenced, no Outlook search available");
                            }
                        }
                        catch (error) {
                            this.logger.error("[TelephonyHybridEventHandler] onConferenceEvent - Impossible to get contact - " + error.message);
                        }
                    }
                })());
            }
        });

        // Get participants asynchronously
        try {
            await Promise.all(participantPromises);
        }
        finally {
            let isSecondaryDevice = false; //NEW_MP1 + CR #3380
            // updtate isSecondaryDevice CRRAINB-3750
            if (primaryOldCall) {
                isSecondaryDevice = isSecondaryDevice || primaryOldCall.isSecondary;//NEW_MP1
            }
            if (secondaryOldCall) {
                isSecondaryDevice = isSecondaryDevice || secondaryOldCall.isSecondary;//NEW_MP1
            }

            // Create the new conference call
            const confParticipantsContacts = confParticipants.map((confParticipant) => { return confParticipant.contact; });
            const newConferenceCall = this.createConferenceCall(newConnectionId, confParticipantsContacts);
            // set remoteParties array
            const remoteParties = confParticipants.map((confParticipant: { contact: Contact; phoneNumber: string; jid: string; direction: string; }) => {
                const computedPhoneNumber = this.computeRemotePartyNumber(confParticipant.jid, confParticipant.phoneNumber);
                this.logger.debug("remoteParties iter: " + confParticipant.phoneNumber + " " + computedPhoneNumber + " " + confParticipant.contact.id + " " + confParticipant.direction);
                return { rawPhoneNumber: confParticipant.phoneNumber, phoneNumber: computedPhoneNumber, contact: confParticipant.contact, direction: confParticipant.direction };
            });
            newConferenceCall.setRemoteParties(remoteParties);
            newConferenceCall.setStatus(CallStatus.ACTIVE);
            newConferenceCall.relevantEquipmentId = CallRB.getDeviceIdFromConnectionId(newConnectionId);

            newConferenceCall.isSecondary = isSecondaryDevice; //NEW_MP1

            this.logger.debug("[TelephonyHybridEventHandler] on conferenced, create new call: " + newConferenceCall);

            if ((primaryOldCall && primaryOldCall.isInExternalWindow) || (secondaryOldCall && secondaryOldCall.isInExternalWindow)) {
                newConferenceCall.isInExternalWindow = true;
            }
            this.sendCallUpdateEvent(newConferenceCall);

            // Release previous calls
            if (primaryOldCall) {
                primaryOldCall.setStatus(CallStatus.ENDED);
                this.logger.debug("[TelephonyHybridEventHandler] on conferenced, updated primaryOldCall: " + primaryOldCall);
                this.sendCallUpdateEvent(primaryOldCall);
            }
            if (secondaryOldCall) {
                secondaryOldCall.setStatus(CallStatus.ENDED);
                this.logger.debug("[TelephonyHybridEventHandler] on conferenced, updated secondaryOldCall: " + secondaryOldCall);
                this.sendCallUpdateEvent(secondaryOldCall);
            }
        }
    }

    /**
     * Handler of CALLSUBJECT event
     * @private
     * @param {any} callElem Callsubject sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onCallSubjectEvent(callElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onCallSubjectEvent");

        try {
            const call = await this.getCall(callElem, true);

            call.subject = callElem.find("subject").text();
            if (call.subject) {
                this.logger.info("[TelephonyHybridEventHandler] subject display" + call.subject);
                call.sendEvent('subject');
                this.sendCallUpdateEvent(call);
            }
        }
        catch (error) {
            const errorMessage = "onCallSubjectEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of CONNECTIONCLEARED event
     * @private
     * @param {any} clearElem Connectioncleared sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onClearCallEvent(clearElem: any): Promise<void> {
        const connectionId = clearElem.attr("callId");
        const callId = CallRB.getIdFromConnectionId(connectionId);
        const cause = clearElem.attr("cause");
        const globalCallId = clearElem.attr('globalCallId') || "";
        this.logger.info("[TelephonyHybridEventHandler] onClearCallEvent, connectionId: " + connectionId);

        try {
            if (this.telephonyService.calls[callId]) {
                const call = await this.getCall(clearElem);

                call.globalCallId = globalCallId; //useful to be able to retrieve this call further by globalCallId (used to compute direction when answering queued call on oxe)
                this.computeCallRelevancy(clearElem, call); //MP_GENE_SEC
                call.lastCause = cause ? cause : "";
                this.telephonyService.clearCall(call, true); //2nd para true for clearcall without broadcast
                this.sendCallUpdateEvent(call, clearElem);
            } else {
                this.logger.info("[TelephonyHybridEventHandler] onClearCallEvent, no call for connectionId: " + connectionId);
            }
            delete this.reservationCalls[callId];
        }
        catch (error) {
            const errorMessage = "onClearCallEvent -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Handler of FORWRDED event
     * @private
     * @param {any} forwardElem Forwarded sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private onCallForwardedEvent(forwardElem: any): Promise<void> {
        const forwardType = forwardElem.attr("forwardType");
        const forwardTo = forwardElem.attr("forwardTo");
        const pbxForwardType = forwardElem.attr("pbxForwardType");
        this.logger.info("[TelephonyHybridEventHandler] onCallForwardedEvent forwardType: " + forwardType + " forwardTo: " + forwardTo + " pbxForwardType: " + pbxForwardType);

        if (pbxForwardType !== "overflow_to_associated") {
            this.eventService.publish("ON_CALL_FORWARDED_EVENT", {
                forwardType: forwardType,
                forwardTo: pbxForwardType !== "do_not_disturb" ? forwardTo : "",
                pbxForwardType: pbxForwardType
            });

            this.telephonyService.forwardObject.forwardType = forwardType;
            this.telephonyService.forwardObject.forwardTo = pbxForwardType !== "do_not_disturb" ? forwardTo : "";
            this.telephonyService.forwardObject.pbxForwardType = pbxForwardType;
        } else if (this.telephonyService.isOxe && (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_OXE_OVERFLOW) || this.settingsService.getBooleanSetting("forwardOnCloudIssue"))) {
            //RQRAINB-8850++
            this.telephonyService.forwardObject.forwardOverflowType = forwardType;
            this.telephonyService.forwardObject.forwardOverflowTo = forwardTo;
            this.telephonyService.forwardObject.pbxForwardOverflowType = pbxForwardType;
            this.eventService.publish("ON_CALL_FORWARDOVERFLOW_EVENT", {
                forwardOverflowType: forwardType,
                forwardOverflowTo: forwardTo,
                pbxForwardOverflowType: pbxForwardType
            });
        }
        return;
        //RQRAINB-8850--
    }

    /**
     * Handler of DYNAMIC STATE event
     * @private
     * @param {any} dynamicStateElem dynamicstate sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private onOxeDynamicStateEvent(dynamicStateElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onOxeDynamicStateEvent received");

        //if the deskphone is ON now
        if (!this.telephonyService.getOxeDynamicStateReceived()) {
            this.telephonyService.setOxeDynamicStateReceived();
            if (dynamicStateElem[0].attributes.length === 0) {
                this.telephonyService.getOxeDynamicState();
            }
        }

        // Handle attributes if there exists in event dynamicState
        const inHuntingGroupAttr: string = dynamicStateElem.attr("inHuntingGroup");
        const ringingActivatedAttr: string = dynamicStateElem.attr("ringingActivated");
        const secretIdentityAttr: string = dynamicStateElem.attr("secretIdentity");//RQRAINB-9582
        const doNotDisturbAttr: string = dynamicStateElem.attr("doNotDisturb");

        if (inHuntingGroupAttr) {
            this.logger.info("[TelephonyHybridEventHandler] onOxeDynamicStateEvent with attribute inHuntingGroup: " + inHuntingGroupAttr);
            this.telephonyService.handleOxeUserGroupData(inHuntingGroupAttr === "true");
        }

        if (ringingActivatedAttr) {
            const deskphoneNonRingingActivated: boolean = ringingActivatedAttr !== "true";
            this.telephonyService.setOxeDeskPhoneNonRinging(deskphoneNonRingingActivated);
            this.telephonyService.publish(RBEvent.create('onSilentRingingUpdatedEvent', deskphoneNonRingingActivated));
            this.logger.info("[TelephonyHybridEventHandler] OxeDynamicStateEvent received: silent ringing: " + deskphoneNonRingingActivated ? "true" : "false");
        }

        //RQRAINB-9582++
        if (secretIdentityAttr && this.telephonyService.isOxe) {
            this.logger.info("[TelephonyHybridEventHandler] onOxeDynamicStateEvent with attribute secretIdentity: " + secretIdentityAttr);
            const oxeSecretIdentityActivated: boolean = secretIdentityAttr === "true";
            if (this.telephonyService.getOxeSecretIdentity() !== oxeSecretIdentityActivated) {
                this.telephonyService.setOxeSecretIdentity(oxeSecretIdentityActivated);
            }
            this.telephonyService.publish(RBEvent.create('onSecretIdentityUpdatedEvent', oxeSecretIdentityActivated)); //RQRAINB-10990
        }//RQRAINB-9582--

        if (doNotDisturbAttr && this.telephonyService.isOxe) {
            this.logger.info("[TelephonyHybridEventHandler] onOxeDynamicStateEvent with attribute doNotDisturb: " + doNotDisturbAttr);
            const oxeDoNotDisturbActivated: boolean = doNotDisturbAttr === "true";
            if (this.telephonyService.getOxeDoNotDisturb() !== oxeDoNotDisturbActivated) {
                this.telephonyService.setOxeDoNotDisturb(oxeDoNotDisturbActivated);
            }
            this.telephonyService.publish(RBEvent.create('onDoNotDisturbUpdateEvent', oxeDoNotDisturbActivated));
        }
        return;
    }

    /**
     * Handler of NOMADICSTATUS event
     * @private
     * @param {any} nomadicElem Nomadicstatus sub element of received event
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async onCallNomadicEvent(nomadicElem: any): Promise<void> {
        this.logger.info("[TelephonyHybridEventHandler] onCallNomadicEvent");

        if (!this.telephonyService.started && !this.telephonyService.starting) {
            //we receive a nomadic status event while the telephony is not yet initialized: initialize it first
            this.logger.info("[TelephonyHybridEventHandler] onCallNomadicEvent on uninitialized telephony => relaunch telephony initialization");
            this.telephonyService.launchTelephonyInitilization();
            return;
        }

        const response = {
            destination: nomadicElem.attr("destination"),
            featureActivated: nomadicElem.attr("featureActivated"),
            makeCallInitiatorIsMain: nomadicElem.attr("makeCallInitiatorIsMain"),
            modeActivated: nomadicElem.attr("modeActivated"),
            nomadicModeInitialized: nomadicElem.attr("nomadicModeInitialized")
        };

        const makeCallInitiatorIsMainChanged = await this.telephonyService.updateNomadicData(response);

        //get connections snapshot when we switch from Office to Nomadic or from Nomadic to Office
        if (makeCallInitiatorIsMainChanged) {
            const snapshotSecondary = this.telephonyService.nomadicObject.featureActivated && this.telephonyService.nomadicObject.modeActivated && !this.telephonyService.nomadicObject.makeCallInitiatorIsMain;
            this.telephonyService.snapshotCalls(snapshotSecondary);
        }
    }

    /*********************************************************************/
    /** UTILITY METHODS                                                 */
    /*********************************************************************/

    /**
     * Publishes a ON_CALL_UPDATED_EVENT event for the given call
     * @private
     * @param {Call} call call to notify
     * @param {any} elem Additional info to publish
     * @memberof TelephonyHybridEventHandler
     */
    private sendCallUpdateEvent(call: CallRB, elem: any = undefined) {
        const infoEvt = this.extractInfoEvent(elem);
        this.logger.info("[TelephonyHybridEventHandler] sendCallUpdateEvent call: " + call + (infoEvt ? (" infoEvt: " + JSON.stringify(infoEvt)) : ""));
        this.eventService.publish("ON_CALL_UPDATED_EVENT", call, infoEvt);
    }

    /**
     * Extract additional info from given event element
     * @private
     * @param {any} elem Event element from which we extract additional info
     * @returns {any} the additional info object
     * @memberof TelephonyHybridEventHandler
     */
    private extractInfoEvent(elem: any): any {
        let infoEvent = { actionElemName: "", cause: "" };
        if (elem && elem !== null) {
            const tagNames = elem.prop("tagName").split(":");
            infoEvent.actionElemName = tagNames[tagNames.length - 1].toLowerCase();
            infoEvent.cause = elem.attr("cause");
            infoEvent.cause = infoEvent.cause ? infoEvent.cause : "";
        }
        else {
            infoEvent = null;
        }
        return infoEvent;
    }

    /**
     * Create a new conference call
     * @private
     * @param {string} connectionId CSTA connection identifier
     * @param {Contact[]} participants List of conferenced participant contacts
     * @returns {Call} the newly created conference call
     * @memberof TelephonyHybridEventHandler
     */
    private createConferenceCall(connectionId: string, participants: Contact[]): CallRB {
        // Create and configure the conference call
        const conferenceCall = CallRB.create(CallStatus.ENDED, null, CallType.PHONE);
        conferenceCall.setConnectionId(connectionId);
        conferenceCall.isConference = true;
        conferenceCall.setParticipants(participants);
        this.telephonyService.calls[conferenceCall.id] = conferenceCall;
        return conferenceCall;
    }

    /**
     * Retrieves the call from the received event element
     * @private
     * @param {any} elem Telephony event element
     * @param {boolean} doNotUseEqtIdFromElem Indicates wether equiment identifier inside connection identifier may be used or not
     * @param {boolean} searchPendingMakeCall Indicates wether the call may be searched in pendingMakeCalls array
     * @param {boolean} noCreate Indicates wether the call may be craetaed if it does not exist
     * @param {boolean} searchReservationCall Indicates wether the call may be searched in reservationCalls array
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the found or created call
     * @memberof TelephonyHybridEventHandler
     */
    private async getCall(elem: any, doNotUseEqtIdFromElem: boolean = undefined, searchPendingMakeCall: boolean = undefined, noCreate: boolean = undefined, searchReservationCall: boolean = undefined): Promise<CallRB> {
        const jid = elem.attr("endpointIm");
        const phoneNumber = elem.attr("endpointTel");
        const connectionId = elem.attr("callId");

        return this.getOrCreateCall(connectionId, jid, phoneNumber, doNotUseEqtIdFromElem, searchPendingMakeCall, noCreate, searchReservationCall);
    }

    /**
     * Retrieves or creates the call from the given parameters
     * @private
     * @param {string} connectionId CSTA connection identifier
     * @param {string} jid Jabber identifier of the other party
     * @param {string} phoneNumber Phone number of the other party
     * @param {boolean} doNotUseEqtIdFromElem Indicates wether equiment identifier inside connection identifier may be used or not
     * @param {boolean} searchPendingMakeCall Indicates wether the call may be searched in pendingMakeCalls array
     * @param {boolean} noCreate Indicates wether the call may be craetaed if it does not exist
     * @param {boolean} searchReservationCall Indicates wether the call may be searched in reservationCalls array
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the found or created call
     * @memberof TelephonyHybridEventHandler
     */
    private async getOrCreateCall(connectionId: string, jid: string = undefined, phoneNumber: string = undefined, doNotUseEqtIdFromElem: boolean = undefined, searchPendingMakeCall: boolean = undefined, noCreate: boolean = undefined, searchReservationCall: boolean = undefined): Promise<CallRB> {

        if (doNotUseEqtIdFromElem) {
            //search call by callId (not by connectionId) : used when the event's connectionId may contain the connection Id of the other party (ex: hold/retrieve)
            const orgConnectionId = connectionId;
            connectionId = Object.keys(this.telephonyService.calls || []).find((callConnectionId) => {
                return CallRB.getCallIdFromConnectionId(callConnectionId) === CallRB.getCallIdFromConnectionId(connectionId);
            });
            this.logger.debug("[TelephonyHybridEventHandler] getOrCreateCall - HOLD/RETRIEVE search call with callId: " + CallRB.getCallIdFromConnectionId(orgConnectionId) + " from connection id: " + orgConnectionId + " => found connectionId: " + connectionId);
        }
        if (!connectionId) {
            throw new Error("no connection id");
        }

        if (searchPendingMakeCall) { //true for initiated and originated events
            // search for a pending makecall
            const call: CallRB = this.telephonyService.pendingMakeCalls.pop() as CallRB;
            if (call) {
                call.setStatus(CallStatus.DIALING);
                call.setConnectionId(connectionId); //set connectionId and id
                call.setRelevantEquipmentId(CallRB.getDeviceIdFromConnectionId(call.connectionId));
                this.logger.info("[TelephonyHybridEventHandler] getOrCreateCall - " + connectionId + " - " + jid + " => found pendingMakeCall: Call confirmed by csta event " + call);

                this.telephonyService.calls[call.id] = call;
                return call;
            }
        }

        if (searchReservationCall) { //true for delivered (incoming) events
            const reservationCall: CallRB = this.findReservationCall(connectionId);
            if (reservationCall) {
                delete this.reservationCalls[connectionId];
                reservationCall.setConnectionId(connectionId); //set connectionId and id
                this.logger.info("[TelephonyHybridEventHandler] getOrCreateCall - " + connectionId + " - " + jid + " => found reservationCall: " + reservationCall);

                this.telephonyService.calls[reservationCall.id] = reservationCall;
                return reservationCall;
            }
        }

        const call = this.telephonyService.calls[connectionId];
        if (call) {
            this.logger.info("[TelephonyHybridEventHandler] getOrCreateCall - " + connectionId + " - " + anonymizer(phoneNumber) + " - " + jid + " => found call: " + call);
            return call;
        } else if (noCreate) {
            // call does not exist, but we must not create it
            this.logger.info("[TelephonyHybridEventHandler] getOrCreateCall - " + connectionId + " => call not found");
            return null;
        }

        // create the call...
        this.logger.info("[TelephonyHybridEventHandler] getOrCreateCall - " + connectionId + " - " + anonymizer(phoneNumber) + " - " + jid + " => not found : create call...");

        if (!jid && !phoneNumber) { phoneNumber = "****"; }
        const contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
        return this.telephonyService.getOrCreateCall(CallStatus.ENDED, connectionId, contact);
    }

    /**
     * Compute call relevant data, such as relevantEquipmentId, connectionId and isSecondary
     * @private
     * @param {any} elem Telephony event element
     * @param {Call} call Call of which the relevant properties are computed
     * @memberof TelephonyHybridEventHandler
     */
    private computeCallRelevancy(elem: any, call: CallRB): void {

        const newCallId = elem.attr("newCallId");
        const connectionId = newCallId ? newCallId : elem.attr("callId");
        let deviceType = elem.attr("deviceType");
        const equipmentId = CallRB.getDeviceIdFromConnectionId(connectionId);
        const nomadicInfo = this.telephonyService.getNomadicObject();
        let makeCallInitiatorIsMain = nomadicInfo.makeCallInitiatorIsMain;

        if (!deviceType) { deviceType = "MAIN"; }
        if (!nomadicInfo.modeActivated) { makeCallInitiatorIsMain = true; }

        // Handle call information
        if (deviceType === "MAIN" && makeCallInitiatorIsMain) {
            call.relevantEquipmentId = equipmentId;
            call.connectionId = connectionId;
        }
        if (deviceType === "SECONDARY" && !makeCallInitiatorIsMain) {
            call.relevantEquipmentId = equipmentId;
            call.connectionId = connectionId;
        }

        call.isSecondary = (deviceType && deviceType === "SECONDARY" && !makeCallInitiatorIsMain);

        this.logger.info("[TelephonyHybridEventHandler] computeCallRelevancy -- relevantEquipmentId: " + call.relevantEquipmentId + " connectionId: " + call.connectionId + " isSecondary: " + call.isSecondary);
    }

    /**
     * Updates a call contact
     * @private
     * @param {string} jid Jabber identifier of other party
     * @param {string} phoneNumber Phone number fof other party
     * @param {any} actionElem Action element of the event
     * @param {Call} call The call to update
     * @async
     * @returns {Promise<void>} Status promise resolved when the update is complete
     * @memberof TelephonyHybridEventHandler
     */
    private async updateCallContact(jid: string, phoneNumber: string, actionElem: any, call: CallRB): Promise<void> {

        const tagNames = actionElem.prop("tagName").split(":");
        const actionElemName = tagNames[tagNames.length - 1].toLowerCase();
        try {
            // Determine if the contact has to be updated from event information
            const updateAnalyse = this.analyzeContactChange(jid, phoneNumber, call);
            // Whatever the contact change, for simple call, after analyse, update at least the call remote phoneNumber
            if (!call.isConference && phoneNumber !== "") {
                call.setRemotePartyPhoneNumberAndJid(phoneNumber, jid, this.computeRemotePartyNumber(jid, phoneNumber));
                this.logger.debug("remoteParty: " + anonymizer(call.remoteParty.rawPhoneNumber) + " " + anonymizer(call.remoteParty.phoneNumber) + " " + call.remoteParty.contact.id);
            }
            if (updateAnalyse) {
                try {
                    const contact: Contact = await this.contactService.getOrCreateContact(jid, phoneNumber);

                    // Outlook Call Party identification
                    if (updateAnalyse.searchOutlookToBeDone) {
                        try {
                            const updateStatus = await this.telephonyService.updateContactFromOutlookInfos(contact, phoneNumber);

                            if (updateStatus) {
                                this.logger.debug("[TelephonyHybridEventHandler] on " + actionElemName + ", update from outlook for contact :" + contact.nameForLogs);
                                this.makeUpdateContact(call, contact, phoneNumber, jid);
                            } else {
                                this.logger.debug("[TelephonyHybridEventHandler] on " + actionElemName + ", no update from outlook for contact :" + contact.nameForLogs);

                                if (updateAnalyse.updateContactToBeDone) {
                                    this.logger.debug("[TelephonyHybridEventHandler] on " + actionElemName + ", update contact :" + contact.nameForLogs);
                                    this.makeUpdateContact(call, contact, phoneNumber, jid);
                                }
                            }
                        }
                        catch (error) {
                            this.logger.debug("[TelephonyHybridEventHandler] on " + actionElemName + ", no Outlook search available");
                            if (updateAnalyse.updateContactToBeDone) {
                                this.logger.debug("[TelephonyHybridEventHandler] on " + actionElemName + ", update contact :" + contact.nameForLogs);
                                this.makeUpdateContact(call, contact, phoneNumber, jid);
                            }
                        }
                        return;
                    }

                    // Only simple updateContactToBeDone no outlook stuff
                    this.logger.debug("[TelephonyHybridEventHandler] on " + actionElemName + ", update contact :" + contact.nameForLogs);
                    this.makeUpdateContact(call, contact, phoneNumber, jid);
                    return;
                }
                catch (error) {
                    if (error) {
                        const errorMessage = "updateCallContact -- " + error.message;
                        this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
                    }
                    this.logger.debug("[TelephonyHybridEventHandler] on " + actionElemName + ", no Outlook search available");
                }
            }
        }
        catch (error) {
            const errorMessage = "updateCallContact -- " + error.message;
            this.logger.error("[TelephonyHybridEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Updates the contact and the remote party data in the call
     * @private
     * @param {Call} call The call to update
     * @param {Contact} contact The contact to set in call and remote party data
     * @param {string} phoneNumber The phone number to set in remote party  data
     * @param {string} jid The jid of the contact, needed to compute the remote party number
     * @memberof TelephonyHybridEventHandler
     */
    private makeUpdateContact(call: CallRB, contact: Contact, phoneNumber: string, jid: string): void {
        call.setContact(contact);
        const computedRemotePartyNumber = this.computeRemotePartyNumber(jid, phoneNumber);
        call.setRemoteParty(phoneNumber, jid, contact, computedRemotePartyNumber);
        this.logger.debug("remoteParty: " + call.remoteParty.rawPhoneNumber + " " + call.remoteParty.phoneNumber + " " + call.remoteParty.contact.id);
    }

    /**
     * Analyse if a contact update operation has to be done following situation
     * @private
     * @param {string} jid Jabber identifier of other party
     * @param {string} phoneNumber Phone number of other party
     * @param {Call} call [required] the call to update
     * @returns {any} Object:{ updateContactToBeDone : boolean, searchOutlookToBeDone :boolean}
     *  updateContactToBeDone true if the contact has to be updated in the call (by setContact)
     *  searchOutlookToBeDone true if an outlook search has to be performed to resolve call identity
     * @memberof TelephonyHybridEventHandler
     */
    private analyzeContactChange(jid: string, phoneNumber: string, call: CallRB): any {

        let updateContact = false;
        let searchOutlook = false;

        if (!jid && !phoneNumber) { //nothing could be analysed then updated
            return null;
        }
        // One2One Call (not a conference) only call.contact is impacted
        if (!call.isConference) {

            // No contact... Whatever situation set contact
            if (!call.contact) {
                return { updateContactToBeDone: true, searchOutlookToBeDone: true };
            }

            // PCG known the distant as a rainbow user (no outlook search is necessary)
            if (jid !== "") {
                // Only change if not the same from previous call setting
                if (call.contact.id !== jid) {
                    updateContact = true;
                    searchOutlook = false;
                }
            }
            // No jid available
            else {
                // call.contact not known as rainbow user, contact.id contain former phoneNumber
                if (call.contact.temp) {
                    if ((call.contact.id !== phoneNumber && call.contact.nameUpdatePrio !== Contact.NameUpdatePrio.SERVER_UPDATE_PRIO) //RQRAINB-10966
                    //called change but no previous directory search result
                    ) {
                        updateContact = true;
                        searchOutlook = true;
                    }
                    else if (call.contact.displayName === phoneNumber) { //no change but name not known
                        updateContact = false;
                        searchOutlook = true; //try an outlook research
                        //searchOutlook = false; //OUTLOOK NOT YET DELIVERED
                    }
                }
                // call.contact known as rainbow user update only if phoneNumber change (except ringing empty case)
                else if (call.getRemotePartyRawPhoneNumber() !== "" && phoneNumber !== "") {
                    if (call.getRemotePartyRawPhoneNumber() !== phoneNumber) {
                        updateContact = true;
                        searchOutlook = true;
                        //searchOutlook = false;	//OUTLOOK NOT YET DELIVERED
                    }
                }
            }
            return (updateContact || searchOutlook) ? { updateContactToBeDone: updateContact, searchOutlookToBeDone: searchOutlook } : null;
        }
        return undefined;
    }

    public computeDisplayName(firstname: string, lastname: string): string {
        const firstName = firstname ? firstname.charAt(0).toUpperCase() + firstname.slice(1) : null;
        const lastName = lastname ? lastname.charAt(0).toUpperCase() + lastname.slice(1) : null;
        let displayName: string = "";

        if (lastName && firstName) {
            if (this.settingsService.getSetting("displayOrder") === "FL") {
                displayName = firstName + " " + lastName;
            } else {
                displayName = lastName + " " + firstName;
            }
        } else if (lastName && !firstName) {
            displayName = lastName;
        } else if (firstName) {
            displayName = firstName;
        } else {
            displayName = "Anonymous";
        }

        return displayName;
    }
}
